// The website service request form (freedom-power-calculator, served from
// book.gofreedompower.com) opened in a portal modal. It is the same form as
// gofreedompower.com/contact; submissions go through fsp-api-hub to HubSpot,
// which creates the service ticket.
//
// The form and its parent page talk over postMessage, the same contract as the
// site's embed/consultation-launcher.html:
//   form -> parent  fp-consult-ready   the form has loaded
//   parent -> form  fp-consult-init    page info, plus `prefill` from the portal
//   form -> parent  fp-consult-event   analytics events for the parent's GTM
//   form -> parent  fp-consult-done    the request was submitted
// The form only accepts init from parent origins it lists (my. and my-dev.),
// and only renders in frames its CloudFront CSP frame-ancestors allows.

export const SERVICE_REQUEST_URL = "https://book.gofreedompower.com/service-request";
// Test page, dry run: shows the payload it would send and submits nothing.
// Use ?fp_test=submit instead for a real ticket marked [TEST].
export const SERVICE_REQUEST_TEST_URL =
  "https://book.gofreedompower.com/service-request-test?fp_test=dry";

// Every portal customer is an installed Freedom Power customer.
export const PORTAL_RELATION = "FSP Customer - Installed";

type Env = Record<string, string | undefined>;

export function serviceRequestUrl(env: Env = process.env): string {
  const override = (env.REACT_APP_SERVICE_REQUEST_URL || "").trim();
  if (override) return override;
  return env.REACT_APP_HOST_ENV === "development"
    ? SERVICE_REQUEST_TEST_URL
    : SERVICE_REQUEST_URL;
}

export function frameOrigin(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return "";
  }
}

export interface ServiceRequestPrefill {
  fullName?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  relation: string;
  sunriseProjectId?: string;
}

const text = (v: unknown): string =>
  typeof v === "string" || typeof v === "number" ? String(v).trim() : "";

// 10 digits, from "5128675309", "+15128675309" or "(512) 867-5309".
export function tenDigitPhone(v: unknown): string {
  let digits = text(v).replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  return digits.length === 10 ? digits : "";
}

// What the portal already knows (state.portal), in the form's prefill shape.
// The form checks every value again and fills only fields still empty, so the
// customer can change anything. Empty values are left out.
//
// The contact details are the signed-in person's: a project's secondary
// contact (state.me.customerId === portal.secondaryContactId) gets their own
// name, email and phone, as on Account > Personal info, so service follows up
// with whoever asked. The address and project are the project's either way.
export function buildServiceRequestPrefill(
  portal: any,
  customerId?: string | null
): ServiceRequestPrefill {
  const p = portal || {};
  const address = p.address && typeof p.address === "object" ? p.address : {};
  const projectId = text(p.id);
  const isSecondary =
    !!customerId &&
    text(p.secondaryContactId) === text(customerId) &&
    !!p.secondaryContact &&
    typeof p.secondaryContact === "object";
  const contact = isSecondary ? p.secondaryContact : p;
  const out: ServiceRequestPrefill = {
    fullName: [text(contact.firstName), text(contact.lastName)].filter(Boolean).join(" "),
    email: text(contact.email),
    phone: tenDigitPhone(contact.phoneNumber),
    address: text(address.address),
    city: text(address.city),
    state: text(address.state).toUpperCase(),
    zip: text(address.zip).slice(0, 5),
    relation: PORTAL_RELATION,
    sunriseProjectId: /^\d{4,10}$/.test(projectId) ? projectId : "",
  };
  (Object.keys(out) as (keyof ServiceRequestPrefill)[]).forEach((k) => {
    if (!out[k]) delete out[k];
  });
  return out;
}

// The page as the ticket records it: no query string or hash.
export function pageUriFor(location: { origin: string; pathname: string }): string {
  return location.origin + location.pathname;
}

export function initMessage(
  prefill: ServiceRequestPrefill,
  page: { pageUri: string; pageName: string }
) {
  return {
    type: "fp-consult-init",
    data: {
      // Full height in the modal: the form scrolls itself and sends no height.
      overlay: true,
      tracking: {},
      wrapper: {},
      pageUri: page.pageUri,
      pageName: page.pageName,
      prefill,
    },
  };
}

export type FrameMessage =
  | { kind: "ready" }
  | { kind: "done" }
  | { kind: "event"; eventName: string; eventData: Record<string, unknown> }
  | null;

// Only messages from our iframe's window and the form's origin count.
export function readFrameMessage(
  event: Pick<MessageEvent, "data" | "origin" | "source">,
  expectedOrigin: string,
  frameWindow: Window | null | undefined
): FrameMessage {
  if (!frameWindow || event.source !== frameWindow) return null;
  if (!expectedOrigin || event.origin !== expectedOrigin) return null;
  const msg = event.data;
  if (!msg || typeof msg !== "object" || typeof msg.type !== "string") return null;
  if (msg.type === "fp-consult-ready") return { kind: "ready" };
  if (msg.type === "fp-consult-done") return { kind: "done" };
  if (msg.type === "fp-consult-event" && typeof msg.eventName === "string") {
    const data =
      msg.eventData && typeof msg.eventData === "object" ? msg.eventData : {};
    return { kind: "event", eventName: msg.eventName, eventData: data };
  }
  return null;
}
